/**
 * Utility functions for IndexedDB operations for PDF projects
 */

const DB_NAME = 'pdf-editor-db';
const DB_VERSION = 1;
const STORE_NAME = 'pdf-projects';

// IndexedDB database instance
let dbInstance: IDBDatabase | null = null;

/**
 * Initialize IndexedDB database
 */
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create object store for PDF projects
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('lastModified', 'lastModified', { unique: false });
      }
    };
  });
};

/**
 * Check if IndexedDB is available
 */
export const isIndexedDBAvailable = (): boolean => {
  try {
    return 'indexedDB' in window && indexedDB !== null;
  } catch {
    return false;
  }
};

/**
 * Store data in IndexedDB
 */
export const setItem = async <T>(key: string, value: T): Promise<boolean> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.put({ id: key, data: value, timestamp: Date.now() });
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(new Error('Failed to store data in IndexedDB'));
    });
  } catch (error) {
    console.error('Failed to save to IndexedDB:', error);
    return false;
  }
};

/**
 * Get data from IndexedDB
 */
export const getItem = async <T>(key: string, defaultValue: T): Promise<T> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.get(key);
    
    return new Promise((resolve) => {
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.data !== undefined) {
          resolve(result.data as T);
        } else {
          resolve(defaultValue);
        }
      };
      request.onerror = () => {
        console.error('Failed to read from IndexedDB');
        resolve(defaultValue);
      };
    });
  } catch (error) {
    console.error('Failed to read from IndexedDB:', error);
    return defaultValue;
  }
};

/**
 * Remove data from IndexedDB
 */
export const removeItem = async (key: string): Promise<boolean> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.delete(key);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(new Error('Failed to remove data from IndexedDB'));
    });
  } catch (error) {
    console.error('Failed to remove from IndexedDB:', error);
    return false;
  }
};

/**
 * Clear all data from IndexedDB
 */
export const clear = async (): Promise<boolean> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.clear();
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(new Error('Failed to clear IndexedDB'));
    });
  } catch (error) {
    console.error('Failed to clear IndexedDB:', error);
    return false;
  }
};

/**
 * Get all keys from IndexedDB
 */
export const getAllKeys = async (): Promise<string[]> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.getAllKeys();
    
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result as string[]);
      request.onerror = () => {
        console.error('Failed to get keys from IndexedDB');
        resolve([]);
      };
    });
  } catch (error) {
    console.error('Failed to get keys from IndexedDB:', error);
    return [];
  }
};

/**
 * Get storage usage information for IndexedDB
 */
export const getUsage = async (): Promise<{ used: number; total: number; available: number }> => {
  try {
    // Try to get storage estimate if available
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const used = estimate.usage || 0;
      const total = estimate.quota || 0;
      return {
        used,
        total,
        available: total - used,
      };
    }
    
    // Fallback - return large values since IndexedDB can store much more than localStorage
    return {
      used: 0,
      total: 100 * 1024 * 1024, // 100MB as a reasonable default
      available: 100 * 1024 * 1024,
    };
  } catch (error) {
    console.error('Failed to get IndexedDB usage:', error);
    return {
      used: 0,
      total: 100 * 1024 * 1024,
      available: 100 * 1024 * 1024,
    };
  }
};

export const indexedDBStorage = {
  setItem,
  getItem,
  removeItem,
  clear,
  getAllKeys,
  getUsage,
  isAvailable: isIndexedDBAvailable,
};